import { useEffect, useRef } from "react";
import Container from "components/base/Container";
import {
  MotionBox as Box,
  staggeredAppear,
  viewAppearDefault,
} from "utils/motion";
import ScrollHint from "components/base/ScrollHint";
import Text from "components/base/Text";
import Image from "components/base/Image";

const fadeIn = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { type: "tween", duration: 0.8, delay: 0.6 },
  },
};

export default function HeroVideoHeader({
  title,
  titleType,
  subtitle,
  description,
  descriptionType,
  cta,
  textShadow,
  align,
  noScrollHint,
  noGradient,
  noPadding,
  height,
  minHeight,
  textAlign,
  alignItems,
  videoSrc,
  noAnimation,
  mobileImage,
}) {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    let blocked = false;

    const playVideo = () => {
      if (video && !blocked) {
        const computedStyle = window.getComputedStyle(video);
        if (computedStyle.display !== "none") {
          video.muted = true;
          video.play().catch((error) => {
            if (error.name === "NotAllowedError") {
              // Autoplay blocked (e.g. Safari Low Power Mode) — stop retrying
              blocked = true;
            } else {
              console.error("Error attempting to play", error);
            }
          });
        }
      }
    };

    // Attempt to play the video on load
    playVideo();

    // Add event listeners to play the video on user interaction
    document.addEventListener("click", playVideo);
    document.addEventListener("touchstart", playVideo);

    return () => {
      document.removeEventListener("click", playVideo);
      document.removeEventListener("touchstart", playVideo);
    };
  }, []);

  return (
    <Box
      width="100%"
      bg="black.900"
      as="section"
      style={{ overflow: "hidden" }}
      position="relative"
    >
      <Box
        position="absolute"
        width="100%"
        height="100%"
        display={["block", "block", "none"]}
      >
        <Image
          priority
          src={mobileImage}
          alt="globe"
          height="100vh"
          objectFit="cover"
          opacity={0.6}
        />
      </Box>
      <Box
        as="video"
        ref={videoRef}
        variants={fadeIn}
        initial="hidden"
        animate="visible"
        loop
        muted
        playsInline
        preload="metadata"
        display={["none", "none", "block"]}
        objectFit="cover"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          minHeight: "100%",
        }}
      >
        <source src={videoSrc} type="video/mp4" />
      </Box>
      <Box
        height={height || "100vh"}
        minHeight={minHeight || ["500px", "550px", "700px"]}
        position="relative"
        overflowX="hidden"
        overflowY="hidden"
      >
        <Box
          width="100%"
          height="100%"
          px={[0, null, 20]}
          position="relative"
          bg={
            noGradient
              ? undefined
              : [
                  "linear-gradient(to top, #030409 30%, rgba(0, 0, 0, 0) 55%);",
                  null,
                  "linear-gradient(to right, rgba(0,0,0,0.3) 40%, rgba(0, 0, 0, 0) 55%), linear-gradient(to bottom, rgba(0, 0, 0, 0) 85%, #030409 100%);",
                ]
          }
        >
          <Container
            position="relative"
            height="100%"
            maxWidth="xl"
            display="flex"
            alignItems={alignItems || [align || "flex-end", null, "center"]}
          >
            <Box
              variants={staggeredAppear}
              initial={noAnimation ? "visible" : "hidden"}
              whileInView="visible"
              viewport={{ once: true }}
              width="100%"
              mx="auto"
              alignItems={align}
              textAlign={textAlign || "left"}
              color="white"
              pb={noPadding ? [12, null, 0] : [2, null, 0]}
            >
              {subtitle && (
                <Text
                  type="title_md"
                  color="primary"
                  variants={viewAppearDefault}
                  maxWidth={["100%", null, textAlign ? "100%" : "50vw"]}
                  mb="xs"
                >
                  {subtitle}
                </Text>
              )}
              <Text
                type={titleType || "headline_md"}
                color="white"
                mb="2"
                variants={viewAppearDefault}
                maxWidth={["100%", null, textAlign ? "100%" : "650px"]}
                textShadow={textShadow || "0 2px 6px rgba(0, 0, 0, 0.8)"}
              >
                {title}
              </Text>
              {description && (
                <Text
                  type={descriptionType || "p_lg"}
                  color="white"
                  variants={viewAppearDefault}
                  maxWidth={["100%", null, textAlign ? "100%" : "650px"]}
                  textShadow={textShadow || "0 2px 6px rgba(0, 0, 0, 0.8)"}
                >
                  {description}
                </Text>
              )}
              {cta && <Box variants={viewAppearDefault}>{cta}</Box>}
            </Box>
            {!noScrollHint && (
              <ScrollHint
                display={["none", null, "block"]}
                position="absolute"
                bottom={0}
                right={20}
              />
            )}
          </Container>
        </Box>
      </Box>
    </Box>
  );
}
